import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Subject, noop } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil, map, take } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  InquiryDateResponse,
  BaseInquiryDate,
  BaseInquiryShiftsWithFlex,
  BaseCoreState,
  baseInquirySelectors,
  ShiftPlanCalendarDateInterface,
  BASE_INQUIRY_CALENDAR_DAY_COLUMN_WIDTH,
  BASE_INQUIRY_CALENDAR_BUFFER_PX_SIZE,
  BaseModalService,
  ShiftTimeResponse,
  ShiftPlanListBubbleItemInterface,
  BaseInquiryScrollService,
  ShiftPlanCalendarRangeInterface,
} from 'base';

@Component({
  selector: 'base-customer-shift-plan-dates-list',
  templateUrl: './base-customer-shift-plan-calendar-dates.component.html',
})
export class BaseCustomerShiftPlanCalendarDatesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('shiftsUpdateModal') shiftsUpdateModal: ElementRef;
  @Input() timeShifts: ShiftTimeResponse[] = [];

  modalRef: NgbModalRef;
  dayColumnWidth = BASE_INQUIRY_CALENDAR_DAY_COLUMN_WIDTH;
  baseInquiryBufferPx = BASE_INQUIRY_CALENDAR_BUFFER_PX_SIZE;
  inquiryDates: InquiryDateResponse[] = [];
  datesArray: BaseInquiryDate[] = [];
  bubbleList: ShiftPlanCalendarDateInterface[] = [];
  data: ShiftPlanCalendarDateInterface;
  currentShift: BaseInquiryShiftsWithFlex;
  shiftTimes: Array<ShiftTimeResponse> = [];
  ranges: ShiftPlanCalendarRangeInterface[] = [];
  visibleDatesAmount: number;
  calendarScrollPosition: number;

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private baseInquiryScrollService: BaseInquiryScrollService
  ) {}

  ngOnInit() {
    this.store
      .select(baseInquirySelectors.getShiftPlanList)
      .pipe(
        takeUntil(this.destroy$),
        map((data) => data.list as ShiftPlanCalendarDateInterface[])
      )
      .subscribe((data) => {
        this.bubbleList = data;
      });

    this.store
      .select(baseInquirySelectors.getShiftPlanRangeList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ranges) => {
        this.ranges = ranges;
      });

    this.baseInquiryScrollService.scrollOffset$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.calendarScrollPosition = value;
    });
  }

  ngAfterViewInit() {
    this.baseInquiryScrollService.visibleDatesQty$.pipe(take(1)).subscribe((value) => {
      this.visibleDatesAmount = value;
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  bubbleClick(shiftType: BaseInquiryShiftsWithFlex, data: ShiftPlanCalendarDateInterface) {
    this.data = data;
    this.currentShift = shiftType;

    this.openEditShiftModal();
  }

  openEditShiftModal() {
    this.modalRef = this.modalService.open(
      {
        headText: 'shiftModalTitle',
        bodyTemplate: this.shiftsUpdateModal,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.modalRef.result.then((date: string | null) => {
      if (date) {
        let getRangeOffset = this.getFirstDateIndexInRange(date);
        if (this.ranges.length === 0) {
          this.baseInquiryScrollService.setSliderOffsetValue(0);
        } else {
          let rangeDuration = 0;
          let endDateOffset = 0;
          let isScrollRequired = false;

          const newlyAddedRange = this.ranges.find((range) => range.rangeData.startDate === date);

          if (newlyAddedRange) {
            rangeDuration = newlyAddedRange.dates.length;
            endDateOffset = this.getFirstDateIndexInRange(newlyAddedRange?.rangeData.endDate as string);
            const visibleDatesAfterEndDate = this.visibleDatesAmount + this.calendarScrollPosition - endDateOffset;

            isScrollRequired =
              visibleDatesAfterEndDate < 5 ||
              rangeDuration > this.visibleDatesAmount ||
              getRangeOffset < this.calendarScrollPosition;
          }

          if (isScrollRequired) {
            setTimeout(() => {
              this.baseInquiryScrollService.setSliderOffsetValue(
                rangeDuration >= this.visibleDatesAmount
                  ? endDateOffset - 6 // Last week should be visible in case added range is bigger than visible dates
                  : getRangeOffset
              );
            });
          }
        }
      }
    }, noop);
  }

  getFirstDateIndexInRange(date: string): number {
    return Math.max(
      0,
      this.bubbleList.findIndex((item) => item.dateItem.dateString === date)
    );
  }

  trackByDay(_: number, item: ShiftPlanCalendarDateInterface) {
    return item.dateItem.dateString;
  }

  trackByBubble(_: number, item: ShiftPlanListBubbleItemInterface) {
    return item.shift;
  }
}
